import {EventProcessStageTitle, Tag} from '@hconnect/common/components'
import {mapTaskRepetitionDtoToTaskRepetition} from '@hconnect/common/mappers'
import {getAssigneesValue} from '@hconnect/common/utils'
import {DateFormat} from '@hconnect/uikit'
import {getPlantName, useUpmPlantsContext} from '@hconnect/upm'
import {FeatureFlagKey, useCommonFeatureFlag} from '@hproduce/feature-flags'
import {Grid} from '@mui/material'
import {getEquipmentLabelDetails} from '@widgets/common-shift-event'
import {cloneDeep, get} from 'lodash'
import moment from 'moment'
import {useCallback, useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../../consts'
import {TaskChecklistsContainer} from '../../containers/TaskChecklistsContainer'
import {
  HProduceTask,
  ProcessWorkOrderAssignee,
  ProcessWorkOrderDto,
  ProcessWorkOrderStatus,
  ProcessWorkOrderUser,
  TaskSource,
  TaskStatus
} from '../../types'
import {ProcessWorkOrderDueDatesPreviewLabel} from '../ProcessWorkOrderDueDatesPreviewLabel'
import {ProcessWorkOrderStatusDropDown} from '../ProcessWorkOrderStatusDropDown'

import {TaskDescription} from './TaskDescription'
import {TaskResolvedMandatoryComment} from './TaskResolvedMandatoryComment'

type Props = {
  processWorkOrderTask: ProcessWorkOrderDto
  plantId: string
  timezone: string
  onStatusChange: (newStatus: ProcessWorkOrderStatus, comment?: string) => void
}

export const ProcessWorkOrderDetails = ({
  processWorkOrderTask,
  plantId,
  timezone,
  onStatusChange
}: Props) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const translate = (key: string) => t(key)
  const noneOfThatLabel = translate('task.noneOfThat')

  const [isCommentDialogOpen, setIsCommentDialogOpen] = useState<boolean>(false)
  const [targetStatus, setTargetStatus] = useState<ProcessWorkOrderStatus | null>(null)

  const handleStatusChange = useCallback(
    (newStatus: ProcessWorkOrderStatus) => {
      const isTaskDone = processWorkOrderTask.status !== newStatus && newStatus === TaskStatus.Done

      if (isTaskDone) {
        setTargetStatus(newStatus)
        setIsCommentDialogOpen(true)
      } else {
        onStatusChange(newStatus)
        setTargetStatus(null)
      }
    },
    [processWorkOrderTask.status, onStatusChange]
  )

  const handleTaskMandatoryCommentSubmit = useCallback(
    (taskResolvedComment: string) => {
      if (targetStatus) {
        onStatusChange(targetStatus, taskResolvedComment)
        setIsCommentDialogOpen(false)
        setTargetStatus(null)
      }
    },
    [targetStatus, onStatusChange]
  )

  const upmPlants = useUpmPlantsContext()

  const assigneesValue: string = useMemo(() => {
    const assignees: ProcessWorkOrderUser[] = (
      cloneDeep(get(processWorkOrderTask, 'assignees')) || []
    ).map((assignee: ProcessWorkOrderAssignee) => assignee.user)
    const label = getAssigneesValue(assignees, t)
    return label ? label : noneOfThatLabel
  }, [noneOfThatLabel, processWorkOrderTask, t])

  const showChecklists = useCommonFeatureFlag(FeatureFlagKey.Checklists, {plantId})

  const defaultRepetitionStartDate = useMemo(
    () => moment.utc(processWorkOrderTask.dueDate).tz(timezone),
    [processWorkOrderTask.dueDate, timezone]
  )

  const taskRepetitionSettings = useMemo(
    () =>
      processWorkOrderTask.repetitionInfo
        ? mapTaskRepetitionDtoToTaskRepetition(processWorkOrderTask.repetitionInfo, timezone)
        : null,
    [processWorkOrderTask.repetitionInfo, timezone]
  )

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <ProcessWorkOrderStatusDropDown
            data-test-id="task-details-status"
            onChange={handleStatusChange}
            value={processWorkOrderTask.status}
          />
        </Grid>
        <Grid item md={12}>
          <Tag label={translate('task.label.dueDate')} data-test-id="task-details-dueDate">
            {
              // If the task has a due date and a do not start before date, show both
              processWorkOrderTask.doNotStartBefore && processWorkOrderTask.dueDate ? (
                <>
                  <DateFormat date={processWorkOrderTask.doNotStartBefore} timezone={timezone} /> -{' '}
                  <DateFormat date={processWorkOrderTask.dueDate} timezone={timezone} />
                </>
              ) : processWorkOrderTask.dueDate ? (
                <DateFormat date={processWorkOrderTask.dueDate} timezone={timezone} />
              ) : (
                noneOfThatLabel
              )
            }
          </Tag>
        </Grid>
        <Grid item md={12}>
          <Tag label={translate('task.label.plant')} data-test-id="task-details-plant">
            {getPlantName(upmPlants, plantId)}
          </Tag>
        </Grid>
        <Grid item xs={12}>
          <Tag
            label={translate('task.label.processStage')}
            data-test-id="task-details-processStage"
          >
            {processWorkOrderTask.processStage ? (
              <EventProcessStageTitle plantId={plantId} stage={processWorkOrderTask.processStage} />
            ) : (
              noneOfThatLabel
            )}
          </Tag>
        </Grid>
        <Grid item xs={12}>
          <Tag
            data-test-id="task-details-mainEquipment"
            label={translate('task.label.mainEquipment')}
          >
            {getEquipmentLabelDetails(processWorkOrderTask.mainEquipment, false)}
          </Tag>
        </Grid>
        <Grid item xs={12}>
          <Tag data-test-id="task-details-equipment" label={translate('task.label.equipment')}>
            {getEquipmentLabelDetails(processWorkOrderTask.equipment)}
          </Tag>
        </Grid>
        <Grid item xs={12}>
          <Tag data-test-id="task-details-assignees" label={translate('task.label.assignee')}>
            {assigneesValue}
          </Tag>
        </Grid>
      </Grid>
      {taskRepetitionSettings && (
        <Grid item xs={12}>
          <Tag label={translate('task.label.recurring')}>
            <ProcessWorkOrderDueDatesPreviewLabel
              settings={taskRepetitionSettings}
              defaultStartDate={defaultRepetitionStartDate}
              omitFirstOccurrence
              plantId={plantId}
              timezone={timezone}
            />
          </Tag>
        </Grid>
      )}
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Tag
            label={translate('task.label.description')}
            descriptionSx={{fontWeight: 400, fontSize: 14}}
            data-test-id="task-details-description"
          >
            <TaskDescription description={processWorkOrderTask.description} />
          </Tag>
        </Grid>
        {showChecklists && (
          <Grid item xs={12}>
            <TaskChecklistsContainer
              task={
                {
                  title: processWorkOrderTask.title,
                  plantId: processWorkOrderTask.plantId,
                  source: TaskSource.PROCESS_WORK_ORDER,
                  externalId: processWorkOrderTask.id
                } as HProduceTask
              }
            />
          </Grid>
        )}
      </Grid>
      <TaskResolvedMandatoryComment
        data-test-id="task-resolved-mandatory-comment-modal"
        open={isCommentDialogOpen}
        onClose={() => setIsCommentDialogOpen(false)}
        onSubmit={handleTaskMandatoryCommentSubmit}
      />
    </>
  )
}
